import React from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import { api, apiError, timeAgo } from "@/lib/api";
import Kpi from "@/components/admin/Kpi";
import { Spinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Billing Sync tab: Stripe webhook registration, event health and the reconcile job.
 *
 * Extracted verbatim from src/pages/Admin.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged - only the file
 * boundary moved.
 */
export default function AdminBillingSync({ sync, busy, setBusy, loadAll, loadSync }) {
  return (
        <div className="mt-6 space-y-6" data-testid="admin-sync">
          {!sync ? (
            <Spinner label="Checking Stripe sync" />
          ) : (
            <>
              <div
                data-testid="sync-status-card"
                className={`sm-card p-6 ${
                  sync.configured ? "bg-[#E4F3EA] border-[#B7DFC7]" : "bg-[#FBF0DC] border-[#E7CF9E]"
                }`}
              >
                <div className="flex items-start gap-3">
                  {sync.configured ? (
                    <CheckCircle2 className="w-6 h-6 text-[#1F7A4D] shrink-0" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-[#B7791F] shrink-0" />
                  )}
                  <div className="flex-1">
                    <h2 className="font-display text-xl font-bold text-[#1F2A2E]">
                      {sync.configured
                        ? "Stripe is syncing memberships automatically"
                        : "Automatic membership sync is not set up yet"}
                    </h2>
                    <p className="mt-1.5 text-sm text-[#4B5A60] leading-6">
                      {sync.configured
                        ? "Renewals, failed payments, cancellations and plan changes update member accounts on their own. No manual checks needed."
                        : "Register this site with Stripe so renewals and failed payments update member accounts without anyone checking."}
                    </p>
                    {sync.endpoint?.url ? (
                      <p className="mt-2 text-xs text-[#4B5A60] break-all">
                        Endpoint: <span className="font-mono">{sync.endpoint.url}</span>
                        {sync.endpoint_live_status ? (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-white text-[#1F6F78] font-semibold">
                            {sync.endpoint_live_status}
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                    {sync.missing_events?.length ? (
                      <p className="mt-2 text-xs text-[#B42318]">
                        Missing events: {sync.missing_events.join(", ")}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    onClick={async () => {
                      setBusy("register");
                      try {
                        const { data } = await api.post("/admin/billing-sync/register", {
                          base_url: window.location.origin,
                        });
                        toast.success(`Stripe now points at ${data.url}`);
                        await loadSync();
                      } catch (e) {
                        toast.error(apiError(e));
                      } finally {
                        setBusy("");
                      }
                    }}
                    disabled={busy === "register"}
                    data-testid="register-webhook-button"
                    className="bg-[#1F6F78] hover:bg-[#185B62] text-white"
                  >
                    {busy === "register" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : sync.configured ? (
                      "Re-point Stripe at this site"
                    ) : (
                      "Turn on automatic sync"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      setBusy("reconcile");
                      try {
                        const { data } = await api.post("/admin/billing-sync/reconcile");
                        toast.success(
                          `Checked memberships: ${data.resynced} refreshed, ${data.expired} expired.`
                        );
                        await loadSync();
                        await loadAll();
                      } catch (e) {
                        toast.error(apiError(e));
                      } finally {
                        setBusy("");
                      }
                    }}
                    disabled={busy === "reconcile"}
                    data-testid="reconcile-button"
                    className="bg-white border-[#E3DED4]"
                  >
                    {busy === "reconcile" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1.5" /> Re-check all memberships now
                      </>
                    )}
                  </Button>
                </div>
                <p className="mt-3 text-xs text-[#4B5A60]">
                  Re-point Stripe after you move to a new domain, otherwise renewals will keep
                  going to the old address. Memberships are also re-checked automatically every 6
                  hours as a safety net.
                  {sync.last_reconcile?.last_run_at
                    ? ` Last check ${timeAgo(sync.last_reconcile.last_run_at)}.`
                    : ""}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Kpi label="Events received" value={sync.events.total} />
                <Kpi label="Processed successfully" value={sync.events.processed} tone="good" />
                <Kpi
                  label="Failed to process"
                  value={sync.events.errored}
                  tone={sync.events.errored ? "danger" : "default"}
                />
              </div>

              <div className="sm-card bg-white overflow-x-auto">
                <h3 className="px-4 pt-4 font-display text-lg font-bold text-[#1F2A2E]">
                  Recent Stripe events
                </h3>
                <table className="mt-2 w-full text-sm">
                  <thead className="bg-[#F7F4EF] text-left">
                    <tr>
                      {["Event", "Status", "Mode", "Received"].map((h) => (
                        <th key={h} className="px-3 py-2.5 font-bold text-[#4B5A60]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E3DED4]">
                    {sync.recent_events.map((e) => (
                      <tr key={e.id} data-testid={`sync-event-${e.id}`}>
                        <td className="px-3 py-2.5 font-semibold text-[#1F2A2E]">{e.type}</td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              e.status === "processed"
                                ? "bg-[#E4F3EA] text-[#14603C]"
                                : "bg-[#FBE6E4] text-[#B42318]"
                            }`}
                          >
                            {e.status === "processed" ? (
                              <CheckCircle2 className="w-3 h-3" />
                            ) : (
                              <XCircle className="w-3 h-3" />
                            )}
                            {e.status}
                          </span>
                          {e.error ? (
                            <span className="ml-2 text-xs text-[#B42318]">{e.error}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2.5 text-[#4B5A60]">
                          {e.livemode ? "live" : "test"}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{timeAgo(e.received_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sync.recent_events.length === 0 && (
                  <p className="px-4 py-6 text-sm text-[#4B5A60]">
                    No Stripe events received yet. They appear here as soon as someone subscribes or
                    a renewal is charged.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
  );
}
